import React, { useState, useEffect } from 'react';
import { Button, Select } from 'antd';
import ApplicationService from '../services/axios.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from './spinner';

const ForecastingVsBudgetNew = () => {
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear, currentYear - 1, currentYear - 2];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(['Q1']);
  const [data, setData] = useState([]);
  const [pivotData, setPivotData] = useState([]);
  const [monthHeaders, setMonthHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const service = new ApplicationService();

  // Month mapping for financial year
  const monthMap = {
    '04': 'Apr', '05': 'May', '06': 'Jun',
    '07': 'Jul', '08': 'Aug', '09': 'Sep',
    '10': 'Oct', '11': 'Nov', '12': 'Dec',
    '01': 'Jan', '02': 'Feb', '03': 'Mar'
  };

  const getMonthsForQuarters = (quarters, year) => {
    const quarterMonths = {
      Q1: ['04', '05', '06'],
      Q2: ['07', '08', '09'],
      Q3: ['10', '11', '12'],
      Q4: ['01', '02', '03']
    };

    let months = [];
    quarters.forEach(q => {
      if (quarterMonths[q]) {
        quarterMonths[q].forEach(month => {
          const actualYear = (q === 'Q4') ? parseInt(year) + 1 : year;
          months.push({
            key: `${actualYear}-${month}`,
            label: monthMap[month],
            month: month,
            year: actualYear
          });
        });
      }
    });
    return months;
  };

  const transformToPivot = (rawData, months) => {
    const customerMap = new Map();

    rawData.forEach(row => {
      if (!customerMap.has(row.customer_id)) {
        customerMap.set(row.customer_id, {
          customer_id: row.customer_id,
          customer_name: row.customer_name,
          months: {}
        });
      }

      const customer = customerMap.get(row.customer_id);
      customer.months[row.month] = {
        budget: parseFloat(row.budget || 0),
        forecast: parseFloat(row.forecast || 0),
        actuals: parseFloat(row.actuals || 0)
      };
    });

    return Array.from(customerMap.values());
  };

  const fetchData = async () => {
    if (selectedQuarter.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        year: selectedYear.toString(),
        quarter: selectedQuarter.join(','),
      };
      
      const response = await service.getBudgetForecastActuals(payload);
      const result = response.data;
      
      if (result.statusCode == '200' && result.data) {
        const months = getMonthsForQuarters(selectedQuarter, selectedYear);
        setMonthHeaders(months);
        
        const pivot = transformToPivot(result.data, months);
        setPivotData(pivot);
        setData(result.data);
        
        toast.success('Data fetched successfully!');
      } else {
        setData([]);
        setPivotData([]);
        setMonthHeaders([]);
        toast.info('No data found');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
      setData([]);
      setPivotData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a month is in the past, current, or future
  const getMonthStatus = (monthKey) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYearMonth = `${currentYear}-${currentMonth}`;
    
    if (monthKey < currentYearMonth) return 'past';
    if (monthKey === currentYearMonth) return 'current';
    return 'future';
  };

  useEffect(() => {
    if (selectedQuarter.length > 0) {
      fetchData();
    }
  }, [selectedYear, selectedQuarter]);

  const handleSearch = () => {
    fetchData();
  };

  return (
    <div className="p-4 bg-[#f9fafb]">
      <h2 className="text-2xl font-bold mb-4">Budget, Forecast & Actuals</h2>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '8px', fontWeight: 'bold' }}>Year:</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ marginRight: '8px', fontWeight: 'bold' }}>Quarter:</label>
            <Select
              mode="multiple"
              placeholder="Select Quarter(s)"
              value={selectedQuarter}
              onChange={(values) => setSelectedQuarter(values)}
              style={{ width: 280 }}
              options={[
                { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                { label: 'Q4 (Jan-Mar)', value: 'Q4' },
              ]}
            />
          </div>

          <div>
            <button
              onClick={handleSearch}
              className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md cursor-pointer hover:bg-[#2D2D2D]"
              style={{ height: '40px' }}
            >
              <span className="text-white">Search</span>
            </button>
          </div>
        </div>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-md">
          {pivotData.length > 0 ? (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#EAEEFF]">
                    <th className="border border-gray-300 px-4 py-2 text-left sticky left-0 bg-[#EAEEFF] z-30">
                      Models
                    </th>
                    {monthHeaders.map((month, idx) => {
                      const status = getMonthStatus(month.key);
                      return (
                        <React.Fragment key={idx}>
                          <th className="border border-gray-300 px-3 py-2 text-center bg-[#E8E8FF]">
                            {month.label} Budget
                          </th>
                          {(status === 'past' || status === 'current') && (
                            <th className="border border-gray-300 px-3 py-2 text-center bg-[#D4A5D4]">
                              {month.label} ACTUAL
                            </th>
                          )}
                          {(status === 'current' || status === 'future') && (
                            <th className="border border-gray-300 px-3 py-2 text-center bg-[#FFE8CC]">
                              {month.label} FC
                            </th>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pivotData.map((customer, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-medium sticky left-0 bg-white z-10">
                        {customer.customer_name}
                      </td>
                      {monthHeaders.map((month, mIdx) => {
                        const monthData = customer.months[month.key] || { budget: 0, actuals: 0, forecast: 0 };
                        const status = getMonthStatus(month.key);
                        
                        return (
                          <React.Fragment key={mIdx}>
                            <td className="border border-gray-300 px-3 py-2 text-right">
                              {monthData.budget.toFixed(2)}
                            </td>
                            {(status === 'past' || status === 'current') && (
                              <td className="border border-gray-300 px-3 py-2 text-right bg-[#F9F0F9]">
                                {monthData.actuals.toFixed(2)}
                              </td>
                            )}
                            {(status === 'current' || status === 'future') && (
                              <td className="border border-gray-300 px-3 py-2 text-right bg-[#FFF8F0]">
                                {monthData.forecast.toFixed(2)}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ForecastingVsBudgetNew;

